import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FiPlus } from 'react-icons/fi';
import CalculationItemRow from './CalculationItemRow';
import CalculationItemManager from './CalculationItemManager';
import { formatCurrency } from '../../../utils/formatters';

const CalculationItemSection = ({ 
  items = [], 
  calculationId, 
  onAddItem, 
  onUpdateItem, 
  onDeleteItem, 
  loading = false, 
  readOnly = false 
}) => {
  const { t } = useTranslation();

  const handleItemCreated = useCallback(() => {
    // Trigger parent to refresh calculation data
    if (onAddItem) {
      onAddItem();
    }
  }, [onAddItem]);

  const handleItemUpdated = useCallback(() => {
    // CalculationItemManager already handles the update API call
    // This callback is triggered after successful update to refresh the UI
    // The parent component (CalculationDetailsPage) handles the actual refresh
    if (onUpdateItem) {
      // Signal that an update occurred so parent can refresh data
      onUpdateItem();
    }
  }, [onUpdateItem]);

  const handleDeleteItem = useCallback(async (itemId) => {
    if (onDeleteItem) {
      await onDeleteItem(itemId);
    }
  }, [onDeleteItem]);

  const calculateTotals = useCallback((items) => {
    return items.reduce((acc, item) => {
      const itemTotal = item.quantity * item.sellPrice;
      
      return {
        totalValue: acc.totalValue + itemTotal,
        totalCustomsValue: acc.totalCustomsValue + (item.customsValue || 0)
      };
    }, { totalValue: 0, totalCustomsValue: 0 });
  }, []);

  // Show loading state
  if (loading && !items?.length) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const totals = calculateTotals(items || []);

  return (
    <div className="mt-8">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium">{t('calculation.title')}</h3>
        {!readOnly && (
          <CalculationItemManager
            calculationId={calculationId}
            onSuccess={handleItemCreated}
            buttonText={t('calculation.addItem')}
          />
        )}
      </div>
      {loading ? (
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
        </div>
      ) : items.length > 0 ? (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('calculation.productCode')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('calculation.productDescription')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('calculation.quantity')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('calculation.listPrice')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('calculation.buyPrice')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('calculation.stockPrice')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('calculation.sellPrice')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('calculation.customsValue')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('calculation.customsAvailability')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('common.total')}
                  </th>
                  {!readOnly && <th scope="col" className="relative px-6 py-3"></th>}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {items.map((item) => (
                  <CalculationItemRow
                    key={item.id}
                    item={item}
                    calculationId={calculationId}
                    onUpdateSuccess={handleItemUpdated}
                    onDelete={handleDeleteItem}
                    readOnly={readOnly}
                  />
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td colSpan={readOnly ? 9 : 10} className="text-right py-3 text-sm font-medium text-gray-900">
                    {t('calculation.customsValue')}:
                  </td>
                  <td className="py-3 text-right text-sm font-semibold text-gray-900">
                    {formatCurrency(totals.totalCustomsValue)}
                  </td>
                  {!readOnly && <td></td>}
                </tr>
                <tr className="bg-gray-100 border-t border-gray-200">
                  <td colSpan={readOnly ? 9 : 10} className="text-right py-3 text-base font-bold text-gray-900">
                    {t('common.total')}:
                  </td>
                  <td className="py-3 text-right text-base font-bold text-gray-900">
                    {formatCurrency(totals.totalValue)}
                  </td>
                  {!readOnly && <td></td>}
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      ) : (
        <div className="text-center py-6 bg-white shadow rounded-lg">
          <p className="text-gray-500">{t('common.noResults')}</p>
        </div>
      )}
    </div>
  );
};

export default CalculationItemSection;
